import { Badge } from "../../../../../ui/components/Badge";
import { Table } from "../../../../../ui/components/Table";
import type { AccountantStatusWorkHistoryEntity } from "../../../domain/entities/AccountantStatusEntity";

function formatBooksStatus(status: string): string {
  const normalized = status.trim();
  if (!normalized) return "";

  // Convert snake_case statuses (e.g. "in_progress") to "In Progress"
  if (normalized.includes("_")) {
    return normalized
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }

  // Convert simple lowercase statuses (e.g. "closed") to "Closed"
  if (/^[a-z]+$/.test(normalized)) {
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  return normalized;
}

interface WorkHistorySectionProps {
  history: AccountantStatusWorkHistoryEntity[];
  loading: boolean;
  error: string | null;
  onSelectMonth: (year: number, month: number) => void;
  getMonthNumberFromAbbreviation: (abbr: string) => number;
}

export function WorkHistorySection({
  history,
  loading,
  error,
  onSelectMonth,
  getMonthNumberFromAbbreviation,
}: WorkHistorySectionProps) {
  if (loading) {
    return <div className="h-56 w-full animate-pulse rounded-md bg-neutral-100" />;
  }
  if (error) {
    return (
      <div className="w-full rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
        <span className="text-body font-body text-error-700">{error}</span>
      </div>
    );
  }
  if (history.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Month-wise Work History
        </span>
        <span className="text-caption font-caption text-subtext-color">
          See how much work was done each month across bills, invoices, and transactions.
        </span>
      </div>
      <div className="flex w-full flex-col items-start gap-2 overflow-x-auto">
        <Table
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Month</Table.HeaderCell>
              <Table.HeaderCell>Bills - Processed / Pending</Table.HeaderCell>
              <Table.HeaderCell>Invoices - Processed / Pending</Table.HeaderCell>
              <Table.HeaderCell>Bank Transactions - Categorized / Review</Table.HeaderCell>
              <Table.HeaderCell>Books Status</Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {history.map((item, idx) => (
            <Table.Row
              key={`${item.month}-${item.year}-${idx}`}
              onClick={() =>
                onSelectMonth(item.year, getMonthNumberFromAbbreviation(item.month))
              }
              className="cursor-pointer hover:bg-neutral-50"
            >
              <Table.Cell>
                <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                  {item.month} {item.year}
                </span>
              </Table.Cell>
              <Table.Cell>
                {item.billsProcessed} / {item.billsPending}
              </Table.Cell>
              <Table.Cell>
                {item.invoicesProcessed} / {item.invoicesPending}
              </Table.Cell>
              <Table.Cell>
                {item.transactionsCategorized} / {item.transactionsReview}
              </Table.Cell>
              <Table.Cell>
                <Badge variant="brand">
                  {formatBooksStatus(item.booksStatus ?? "")}
                </Badge>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>
      </div>
    </div>
  );
}
